import React, { useCallback, useState } from "react";

import { defineMessage, useIntl } from "react-intl";

import { GuideStatus } from "@mapmycustomers/shared";
import { Modal } from "@mapmycustomers/ui";

import VideoGuideDescriptor from "@app/component/flows/types/VideoGuideDescriptor";
import getNextGuideStatus from "@app/component/flows/utils/getNextGuideStatus";
import HlsVideo from "@app/component/HlsVideo";
import useGuides from "@app/util/contexts/useGuides";

import styles from "./VideoGuideRunner.module.scss";

const playbackErrorMessage = defineMessage({
  id: "guides.video.playbackError",
  defaultMessage: "This video couldn't be loaded. Please try again later.",
  description: "Shown inside a guide popup when its training video fails to play",
});

// Guide videos are portrait screencasts recorded on mobile, and 360x446 is the only rendition
// CloudFront serves — there is no sharper source to switch to. So the popup is sized off the
// video rather than the other way round: the video fills the vertical space the viewport
// allows and the panel is exactly wide enough to hold it, plus the body's side padding. Every
// number comes from the stylesheet's `:export` block, which owns them. The three bounds are:
//   - preferred: the video's width at `100vh - videoChromeHeight` tall, the height it renders at
//   - upper:     2x native, past which an upscaled screencast's text turns mushy
//   - lower:     the width the popup used at every size before, so a viewport too short to be
//                worth hugging keeps today's layout — a centered video — instead of shrinking
//                the title bar down with it
const MODAL_WIDTH = `clamp(${styles.panelMinWidth}, calc((100vh - ${styles.videoChromeHeight}) * ${styles.videoNativeWidth} / ${styles.videoNativeHeight} + ${styles.panelPadding}), calc(${styles.panelMaxVideoWidth} + ${styles.panelPadding}))`;

export interface VideoGuideRunnerProps {
  descriptor: VideoGuideDescriptor;
}

const VideoGuideRunner = ({ descriptor }: VideoGuideRunnerProps) => {
  const intl = useIntl();
  const { getGuideStatus, hideGuide, updateGuideStatus, visibility } = useGuides();
  const [hasError, setHasError] = useState(false);
  const handleError = useCallback(() => setHasError(true), []);

  // Watching the video is the entire guide, so it completes when playback reaches the end —
  // not when the popup closes, which says nothing about whether it was watched. A viewer who
  // closes early keeps the guide open to come back to.
  const handleEnded = useCallback(() => {
    const guideStatus = getGuideStatus(descriptor.id);
    if (!guideStatus) {
      return;
    }
    const nextStatus = getNextGuideStatus(guideStatus.status, GuideStatus.COMPLETED);
    if (nextStatus !== guideStatus.status) {
      updateGuideStatus({ ...guideStatus, status: nextStatus });
    }
  }, [descriptor.id, getGuideStatus, updateGuideStatus]);

  const handleHide = useCallback(() => {
    // Unlike a conditionally-rendered modal, the runner stays mounted between openings, so
    // a failure has to be cleared here or it would suppress every later playback.
    setHasError(false);
    hideGuide(descriptor.id);
  }, [descriptor.id, hideGuide]);

  return visibility[descriptor.id] ? (
    <Modal
      destroyOnClose
      footer={null}
      onCancel={handleHide}
      open
      // Clips the body to the panel, so tall content can never paint outside it.
      scrollable
      title={intl.formatMessage(descriptor.title)}
      width={MODAL_WIDTH}
    >
      {hasError ? (
        <p className={styles.error}>{intl.formatMessage(playbackErrorMessage)}</p>
      ) : (
        <HlsVideo
          autoPlay
          className={styles.video}
          onEnded={handleEnded}
          onError={handleError}
          url={descriptor.url}
        />
      )}
      <p className={styles.description}>{intl.formatMessage(descriptor.description)}</p>
    </Modal>
  ) : null;
};

export default VideoGuideRunner;
